import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  InputBase,
  Popover,
  Typography,
} from '@mui/material';
import {
  CheckBox as CheckedIcon,
  CheckBoxOutlineBlank as UncheckedIcon,
  DoNotDisturbAlt as ExcludeIcon,
} from '@mui/icons-material';
import {
  StagedFilterFieldDef,
  StagedFilterOption,
  StagedFilterValue,
  cycleStagedIssueFilterValue,
  stagedIssueFilterMode,
} from '../../utils/stagedFilterState';

export interface FilterValueMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  field: StagedFilterFieldDef;
  /** Resolved options — brand's list comes from the queue, the rest are static. */
  options: StagedFilterOption[];
  value: StagedFilterValue;
  onChange: (value: StagedFilterValue) => void;
  /** Facet counts for the current query minus this field. */
  counts?: Record<string, number>;
}

const MENU_WIDTH = 290;

/**
 * Value menu for one filter. Single-select applies and closes; multi-select
 * stays open so several values can be stacked in one visit.
 *
 * Brand-style fields (`exclusiveDefault`) split the row: the name selects only
 * that value, the checkbox adds to a multi-value selection — brands are usually
 * queried one at a time and occasionally several.
 */
export default function FilterValueMenu({
  open,
  anchorEl,
  onClose,
  field,
  options,
  value,
  onChange,
  counts,
}: FilterValueMenuProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const searchable = field.multi;
  const listId = `staged-filter-${field.key}-options`;
  const optionId = (index: number) => `${listId}-option-${index}`;

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  const visible = useMemo(() => {
    const typed = query.trim();
    const q = typed.toLowerCase();
    const filtered = !q
      ? options
      : options.filter((o) => o.label.toLowerCase().includes(q));
    // Brand (and other exclusive type-aheads) can apply a typed value that is
    // not on the current page — All is newest-first, so a brand can be missing.
    if (
      q &&
      field.exclusiveDefault &&
      !filtered.some(
        (o) => o.label.toLowerCase() === q || o.value.toLowerCase() === q,
      )
    ) {
      return [...filtered, { value: typed, label: typed }];
    }
    return filtered;
  }, [options, query, field.exclusiveDefault]);

  const selected = useMemo(
    () => new Set(Array.isArray(value) ? value : [String(value)]),
    [value],
  );

  const commit = (next: StagedFilterValue, close: boolean) => {
    onChange(next);
    if (close) onClose();
  };

  /** Row click: single picks, brand picks only-this, plain multi toggles. */
  const selectOption = (option: StagedFilterOption) => {
    if (!field.multi) {
      commit(option.value, true);
      return;
    }
    const current = Array.isArray(value) ? value : [];
    if (field.exclusiveDefault) {
      const soleSelection = current.length === 1 && current[0] === option.value;
      commit(soleSelection ? [] : [option.value], false);
      return;
    }
    if (field.allowExclude) {
      commit(cycleStagedIssueFilterValue(current, option.value), false);
      return;
    }
    commit(
      current.includes(option.value)
        ? current.filter((v) => v !== option.value)
        : [...current, option.value],
      false,
    );
  };

  /** Checkbox: always additive, never replaces the selection. */
  const toggleOption = (option: StagedFilterOption) => {
    const current = Array.isArray(value) ? value : [];
    commit(
      current.includes(option.value)
        ? current.filter((v) => v !== option.value)
        : [...current, option.value],
      false,
    );
  };

  const moveActive = (delta: number) => {
    if (visible.length === 0) return;
    setActiveIndex((prev) => {
      const next = (prev + delta + visible.length) % visible.length;
      const row = listRef.current?.children[next] as HTMLElement | undefined;
      row?.scrollIntoView({ block: 'nearest' });
      return next;
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveActive(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveActive(-1);
    } else if (event.key === 'Enter') {
      const option = visible[activeIndex];
      if (option) {
        event.preventDefault();
        selectOption(option);
      }
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          onKeyDown: handleKeyDown,
          sx: {
            mt: 0.75,
            width: MENU_WIDTH,
            p: 0.75,
            borderRadius: 2.25,
            boxShadow: '0 14px 36px rgba(15, 23, 42, 0.15)',
          },
        },
      }}
    >
      {searchable && (
        <InputBase
          autoFocus
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
          }}
          placeholder={field.searchPlaceholder || 'Search'}
          inputProps={{
            'aria-label': `Search ${field.label} options`,
            'aria-controls': listId,
            // Arrow keys move the highlight while focus stays in this input.
            'aria-activedescendant': visible[activeIndex] ? optionId(activeIndex) : undefined,
          }}
          sx={{
            width: '100%',
            mb: 0.5,
            px: 1.125,
            height: 30,
            fontSize: '0.8125rem',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            '&.Mui-focused': { borderColor: 'primary.main' },
          }}
        />
      )}

      {field.multi && (
        <Typography variant="caption" sx={{ display: 'block', px: 1.125, pb: 0.5, color: 'text.disabled' }}>
          {field.exclusiveDefault
            ? 'Click a name to select it · tick the box to add more'
            : field.allowExclude
              ? 'Click to show rows with the issue · click again to hide them'
              : 'Matches rows with any of the values you pick'}
        </Typography>
      )}

      <Box
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label={field.label}
        aria-multiselectable={field.multi || undefined}
        sx={{ display: 'flex', flexDirection: 'column', gap: '1px', maxHeight: 264, overflowY: 'auto' }}
      >
        {visible.map((option, index) => {
          const issueMode = field.allowExclude
            ? stagedIssueFilterMode(Array.isArray(value) ? value : [], option.value)
            : selected.has(option.value)
              ? 'include'
              : 'off';
          const isSelected = issueMode !== 'off';
          const isExclude = issueMode === 'exclude';
          const count = counts?.[option.value];
          return (
            <Box
              key={option.value}
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: 1.5,
                flex: '0 0 auto',
                bgcolor: index === activeIndex ? 'action.hover' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {field.exclusiveDefault && (
                <Checkbox
                  size="small"
                  checked={isSelected}
                  onChange={() => toggleOption(option)}
                  onMouseEnter={() => setActiveIndex(index)}
                  inputProps={{
                    'aria-label': isSelected
                      ? `Remove ${option.label} from selection`
                      : `Add ${option.label} to selection`,
                  }}
                  sx={{ p: 0.5, ml: 0.25 }}
                />
              )}
              <Box
                component="button"
                type="button"
                role="option"
                id={optionId(index)}
                aria-selected={isSelected}
                aria-label={
                  isExclude ? `Hide rows with ${option.label}` : undefined
                }
                onClick={() => selectOption(option)}
                onMouseEnter={() => setActiveIndex(index)}
                sx={{
                  flex: '1 1 auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: 0,
                  textAlign: 'left',
                  border: 'none',
                  background: 'none',
                  borderRadius: 1.5,
                  px: 1.125,
                  py: 0.75,
                  cursor: 'pointer',
                  font: 'inherit',
                  fontSize: '0.8125rem',
                  fontWeight: isSelected ? 500 : 400,
                  color: isExclude ? 'error.dark' : isSelected ? 'primary.main' : 'text.primary',
                }}
              >
                {field.multi && !field.exclusiveDefault && (
                  /* Decorative: the whole row is the control, so this is not a second tab stop. */
                  <Box
                    component={isExclude ? ExcludeIcon : isSelected ? CheckedIcon : UncheckedIcon}
                    aria-hidden
                    sx={{
                      flex: '0 0 auto',
                      fontSize: 18,
                      color: isExclude ? 'error.main' : isSelected ? 'primary.main' : 'text.disabled',
                    }}
                  />
                )}
                <Box component="span" sx={{ flex: '1 1 auto', minWidth: 0 }}>
                  {option.label}
                </Box>
                {isExclude && (
                  <Box
                    component="span"
                    sx={{
                      flex: '0 0 auto',
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: 'error.main',
                    }}
                  >
                    Hide
                  </Box>
                )}
                {count != null && (
                  <Box
                    component="span"
                    sx={{
                      flex: '0 0 auto',
                      fontSize: '0.71875rem',
                      color: 'text.disabled',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {count}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
        {visible.length === 0 && (
          <Typography variant="body2" sx={{ px: 1.125, py: 0.875, color: 'text.disabled' }}>
            No matches
          </Typography>
        )}
      </Box>
    </Popover>
  );
}
